import type { Dictionary } from './en';

/**
 * Russian.
 *
 * Typed as `Dictionary`, so a key added to `en.ts` and forgotten here fails the
 * build rather than reaching a reader.
 *
 * Russian takes four plural forms and `Intl.PluralRules` picks between them:
 * `one` for 1, 21, 31…; `few` for 2–4, 22–24…; `many` for 0, 5–20, 25–30…;
 * `other` for fractions. Choosing by `count === 1` — which is what most
 * hand-rolled i18n does — is wrong for every number above four.
 */
export const ru: Dictionary = {
  app: {
    built: 'Собрано {date}',
    name: 'Flightplan',
    tagline: 'Операционный контроль',
  },

  theme: {
    light: 'Светлая тема',
    dark: 'Темная тема',
  },

  language: {
    label: 'Язык',
  },

  auth: {
    username: 'Логин',
    password: 'Пароль',
    signIn: 'Войти',
    signingIn: 'Вход…',
    signOut: 'Выйти',
    showPassword: 'Показать пароль',
    hidePassword: 'Скрыть пароль',
    invalidCredentials: 'Логин или пароль неверны, либо учетная запись заблокирована.',
    networkError: 'Сервер недоступен. Проверьте соединение и повторите попытку.',
    serverError: 'Сервер не смог обработать запрос. Повторите попытку позже.',
    unexpectedResponse: 'Сервер ответил в формате, который приложение не распознало.',
    sessionExpired: 'Сеанс завершен. Войдите снова.',
    notPermitted: 'Этот раздел недоступен для вашей роли.',
    role: 'Роль',
    noRole: 'Без роли',
  },
  nav: {
    styleGuide: 'Дизайн-система',
    onlineTable: 'Расписание рейсов',
    flightMap: 'Карта рейсов',

    internationalArrivals: 'Прибытия международных рейсов',
    airports: 'Аэропорты',
    airlines: 'Авиакомпании',
    integrationMonitor: 'Мониторинг интеграций',
    userManagement: 'Пользователи',
    administration: 'Администрирование',
    profile: 'Ваша учетная запись',
    elkDashboard: 'Дашборд ELK',
    openMenu: 'Открыть меню',
    closeMenu: 'Закрыть меню',
  },

  profile: {
    title: 'Ваша учетная запись',
    lead: 'Ваше имя, а также смена пароля. Изменять это можете только вы — чужие записи администратор правит в разделе «Пользователи».',
    account: 'Учетная запись',
    adminOnlyNote:
      'Логин и роль задает администратор — здесь их изменить нельзя.',
    changePassword: 'Смена пароля',
    currentPassword: 'Текущий пароль',
    newPassword: 'Новый пароль',
    confirmPassword: 'Повторите новый пароль',
    passwordMismatch: 'Пароли не совпадают.',
    saved: 'Учетная запись обновлена.',
    nothingChanged: 'Ничего не изменилось, запрос не отправлен.',
    saveFailed: 'Сервер отклонил изменение, не указав поле с ошибкой.',
    networkError: 'Сервер недоступен. Проверьте соединение и повторите попытку.',
    serverError: 'Сервер не смог обработать запрос. Повторите попытку позже.',
    errorLoad: 'Не удалось загрузить вашу учетную запись. Проверьте соединение и повторите попытку.',
    flightAccess: 'Доступ к рейсам',
    flightAccessSees: 'Вы видите',
    flightAccessAll: 'Вы видите все рейсы.',
    flightAccessAdmin: 'Вы видите все рейсы: доступ администратора не ограничивается.',
    flightAccessRestricted: 'Вы видите только эти рейсы. Доступ задает администратор — обратитесь к нему, если нужно больше.',
    flightAccessFailed: 'Не удалось загрузить ваш доступ к рейсам.',
  },

  flightAccess: {
    all: 'Все рейсы',
    airportsOnly: 'Все рейсы в аэропорт или из аэропорта {airports}',
    airlinesOnly: 'Все рейсы авиакомпании {airlines}',
    both: 'Рейсы авиакомпании {airlines} в аэропорт или из аэропорта {airports}',
    incomplete: 'Ни одного рейса — выберите хотя бы один аэропорт или одну авиакомпанию',
    or: 'или',
    yours: 'Ваши рейсы',
    yoursHint: 'Вы видите только эти рейсы — остальные сервер не показывает. Доступ задает администратор; обратитесь к нему, если нужно больше.',
    emptyHint: 'Вы видите только рейсы из вашего доступа, указанного выше. Фильтр за его пределами ничего не покажет.',
    allYourStations: 'Все ваши аэропорты',
  },

  airlines: {
    title: 'Авиакомпании',
    add: 'Добавить авиакомпанию',
    found: 'Авиакомпаний',
    searchPlaceholder: 'Код, префикс или название',
    allStates: 'Все перевозчики',
    resetFilters: 'Сбросить все',
    sortHint: 'Сортировать по этому столбцу',
    nothingFound: 'Ни одна авиакомпания не подходит под фильтры',
    notFound: 'Этой авиакомпании нет в справочнике — возможно, она открыта по устаревшей ссылке или удалена.',
    errorLoad: 'Не удалось загрузить справочник. Проверьте соединение и повторите попытку.',
    errorForbidden: 'Справочник авиакомпаний недоступен для вашей роли.',
    states: {
      working: 'В рабочем списке',
      notWorking: 'Вне списка',
    },
    columns: {
      iata: 'IATA',
      icao: 'ICAO',
      name: 'Авиакомпания',
      prefix: 'Префикс',
      working: 'Рабочий список',
    },
    flash: {
      created: 'Авиакомпания {code} создана.',
      saved: 'Изменения авиакомпании {code} сохранены.',
      deleted: 'Авиакомпания {code} удалена.',
    },
    panel: {
      create: 'Новая авиакомпания',
      createAction: 'Создать авиакомпанию',
      edit: 'Изменение авиакомпании',
      noCode: 'Без кода',
      readOnly: 'Только просмотр. Изменять справочник авиакомпаний может только администратор.',
      codes: 'Коды',
      names: 'Названия',
      details: 'Статус и примечания',
      iataLength: 'Ровно {count} символа.',
      icaoLength: 'Ровно {count} символа либо пусто.',
      title: 'Краткое название',
      fullTitle: 'Полное название',
      description: 'Примечания',
      saving: 'Сохранение…',
      saveFailed: 'Сервер отклонил изменение, не указав причину.',
      forbidden: 'Изменять справочник авиакомпаний может только администратор.',
      networkError: 'Сервер недоступен. Проверьте соединение и повторите попытку.',
      serverError: 'Сервер не смог обработать запрос. Повторите попытку позже.',
      deleteTitle: 'Удалить авиакомпанию',
      deleteHint: 'Запись удаляется безвозвратно, признака активности здесь нет. Чтобы убрать перевозчика из рабочего списка, не удаляя его, выберите «Вне списка» выше.',
      deleteConfirm: 'Удалить {code} безвозвратно? За записью ничего не подчищается — в расписании код перевозчика хранится обычным текстом, поэтому у всех выполненных рейсов останется код, за которым ничего нет.',
      deleteConfirmAction: 'Да, удалить',
      deleteFailed: 'Авиакомпания не удалена.',
    },
  },

  internationalArrivals: {
    title: 'Международные прилеты',
    errorLoad: 'Не удалось загрузить журнал. Проверьте соединение и повторите попытку.',
    errorForbidden: 'Журнал международных прилетов недоступен для вашей роли.',
    resetFilters: 'Сбросить все',
    sortHint: 'Сортировать по этой колонке',
    allStations: 'Все аэропорты',
    nothingFound: 'Нет прилетов по этим фильтрам',
    rowsFound: 'Прилетов',
    staFrom: 'По расписанию с',
    staTo: 'По расписанию по',
    state: {
      label: 'Строки',
      active: 'Только активные',
      withdrawn: 'Только снятые',
      all: 'Активные и снятые',
    },
    columns: {
      airportIataCode: 'Аэропорт',
      flt: 'Рейс',
      orgn: 'Аэропорт вылета',
      sta: 'Дата по расписанию',
      ata: 'Фактическая дата',
      br: 'Прибытие ВС на стоянку',
      firstPax: 'Первый пасс.',
      lastPax: 'Последний пасс.',
      firstBag: 'Первый багаж',
      lastBag: 'Последний багаж',
      ttlPax: 'Пасс. всего',
      ttlBag: 'Багаж всего',
      spot: 'Стоянка',
      acType: 'Тип ВС',
    },
  },
  airports: {
    title: 'Аэропорты',
    add: 'Добавить аэропорт',
    found: 'Аэропортов',
    searchPlaceholder: 'Код, аэропорт, город или страна',
    countryPlaceholder: 'Код или название',
    allStates: 'Любой статус',
    resetFilters: 'Сбросить все',
    sortHint: 'Сортировать по этому столбцу',
    nothingFound: 'Ни один аэропорт не подходит под фильтры',
    notFound: 'Этого аэропорта нет на текущей странице — возможно, он открыт по устаревшей ссылке или удален.',
    errorLoad: 'Не удалось загрузить справочник. Проверьте соединение и повторите попытку.',
    errorForbidden: 'Справочник аэропортов недоступен для вашей роли.',
    errorSort: 'Сервер не сортирует по этому столбцу. Выберите другой.',
    states: {
      active: 'Активный',
      inactive: 'Неактивный',
    },
    columns: {
      iata: 'IATA',
      icao: 'ICAO',
      name: 'Аэропорт',
      city: 'Город',
      country: 'Страна',
      timeZone: 'Часовой пояс',
      status: 'Статус',
      created: 'Создан',
      updated: 'Изменен',
    },
    flash: {
      created: 'Аэропорт {code} создан.',
      saved: 'Изменения аэропорта {code} сохранены.',
      deleted: 'Аэропорт {code} удален.',
    },
    panel: {
      create: 'Новый аэропорт',
      createAction: 'Создать аэропорт',
      edit: 'Изменение аэропорта',
      noCode: 'Без кода',
      readOnly: 'Только просмотр. Изменять справочник аэропортов может только администратор.',
      codes: 'Коды',
      airportName: 'Название аэропорта',
      place: 'Расположение',
      inEnglish: 'На английском',
      inRussian: 'На русском',
      inKazakh: 'На казахском',
      countryCode: 'Код страны',
      timeZoneSearch: 'Поиск: Almaty, +5',
      timeZoneNone: 'Нет подходящего часового пояса',
      timeZoneClear: 'Очистить часовой пояс',
      latitude: 'Широта',
      longitude: 'Долгота',
      saving: 'Сохранение…',
      missingRequired: 'Заполните отмеченные ниже поля.',
      saveFailed: 'Сервер отклонил изменение, не указав причину.',
      forbidden: 'Изменять справочник аэропортов может только администратор.',
      networkError: 'Сервер недоступен. Проверьте соединение и повторите попытку.',
      serverError: 'Сервер не смог обработать запрос. Повторите попытку позже.',
      deleteTitle: 'Удалить аэропорт',
      deleteHint: 'Запись удаляется безвозвратно. Чтобы перестать предлагать аэропорт, не удаляя его, установите статус «Не предлагается» выше.',
      deleteConfirm: 'Удалить {code} безвозвратно? Это действие нельзя отменить, а у всех рейсов, уже сопоставленных с этим кодом, за ним ничего не останется.',
      deleteConfirmAction: 'Да, удалить',
      deleteFailed: 'Аэропорт не удален.',
    },
  },

  users: {
    title: 'Пользователи',
    add: 'Добавить пользователя',
    found: {
      one: '{count} пользователь',
      few: '{count} пользователя',
      many: '{count} пользователей',
      other: '{count} пользователя',
    },
    ofTotal: 'из {total}',
    searchPlaceholder: 'Логин или имя',
    allRoles: 'Все роли',
    allStates: 'Любой статус',
    resetFilters: 'Сбросить все',
    sortHint: 'Сортировать по этому столбцу',
    nothingFound: 'Ни один пользователь не подходит под фильтры',
    notFound: 'Такого пользователя нет в списке — возможно, он открыт по устаревшей ссылке.',
    errorLoad: 'Не удалось загрузить пользователей. Проверьте соединение и повторите попытку.',
    errorForbidden: 'Список пользователей доступен только администратору.',
    allAccess: 'Любой доступ к рейсам',
    states: {
      active: 'Активен',
      blocked: 'Заблокирован',
    },
    accessStates: {
      all: 'Все рейсы',
      restricted: 'Ограничен',
    },
    roles: {
      ADMIN: 'Администратор',
      AAK: 'ААК',
      USER: 'Пользователь',
      KGA_USER: 'Пользователь КГА',
      INTEGRATOR: 'Интегратор',
      OPERATOR: 'Оператор',
      DISPLAY: 'Экран',
    },
    columns: {
      username: 'Логин',
      name: 'Имя',
      role: 'Роль',
      access: 'Доступ к рейсам',
      status: 'Статус',
      created: 'Создан',
    },
    flash: {
      created: 'Пользователь {name} создан.',
      saved: 'Изменения пользователя {name} сохранены.',
      blocked: '{name} больше не может войти.',
      unblocked: '{name} снова может войти.',
      savedAndSignedOut: 'Изменения {name} сохранены. {name} выведен из системы на всех устройствах.',
      sessionsEnded: '{name} выведен из системы на всех устройствах.',
      createdWithoutAccess: 'Пользователь {name} создан, но доступ к рейсам не сохранен. Пока доступ не сохранен, {name} видит все рейсы.',
    },
    access: {
      title: 'Доступ к рейсам',
      all: 'Все рейсы',
      allHint: 'Видит все рейсы.',
      selected: 'Только выбранные рейсы',
      selectedHint: 'Видит только рейсы, которые подходят под аэропорты и авиакомпании ниже.',
      airports: 'Аэропорты',
      airportsHint: 'Рейс вылетает из одного из них или прилетает в него. Пусто — любой аэропорт.',
      airlines: 'Авиакомпании',
      airlinesHint: 'Рейс выполняет одна из них. Пусто — любая авиакомпания.',
      airportSearch: 'Код или город',
      airlineSearch: 'Код или название',
      remove: 'Убрать {code}',
      offList: 'нет в рабочем списке',
      sees: 'Видит',
      needOne: 'Выберите хотя бы один аэропорт или одну авиакомпанию.',
      save: 'Сохранить доступ',
      saved: 'Доступ к рейсам сохранен.',
      nothingChanged: 'Доступ не изменился, запрос не отправлен.',
      saveFailed: 'Сервер отклонил доступ, не указав причину.',
      changed: 'Изменен {date}, {name}',
      neverSet: 'Не задавался: по умолчанию — все рейсы.',
      adminNote: 'Администратор всегда видит все рейсы, его доступ ограничить нельзя.',
      adminStored: 'Для этой учетной записи сохранено ограничение — {description}. Пока роль «Администратор», оно не действует; если роль снять, оно снова вступит в силу.',
      clearStored: 'Удалить сохраненное ограничение',
      cleared: 'Сохраненное ограничение удалено.',
      adminIgnored: 'ограничение не действует — администратор',
      errorLoad: 'Не удалось загрузить доступ к рейсам. Проверьте соединение и повторите попытку.',
      listFailed: 'Не удалось загрузить доступ пользователей к рейсам, поэтому столбец пуст и доступ изменить нельзя. Обновите, чтобы повторить.',
      retry: 'Повторить',
      createNote: 'Сохраняется сразу после создания пользователя — до того, как вы передадите ему пароль.',
    },
    panel: {
      create: 'Новый пользователь',
      createAction: 'Создать пользователя',
      edit: 'Изменение пользователя',
      details: 'Данные',
      you: 'Вы',
      account: 'Учетная запись',
      username: 'Логин',
      name: 'Имя',
      nameHint: 'ФИО человека или название учетной записи — например, «Диспетчерская ALA».',
      password: 'Пароль',
      passwordHint: 'Не менее {count} символов.',
      passwordTooShort: 'Не менее {count} символов.',
      newPassword: 'Сбросить пароль',
      role: 'Роль',
      roleSelf: 'Свою роль изменить нельзя — это должен сделать другой администратор.',
      noRole: 'Без роли',
      created: 'Создан',
      updated: 'Изменен',
      saving: 'Сохранение…',
      nothingChanged: 'Ничего не изменилось, запрос не отправлен.',
      saveFailed: 'Сервер отклонил изменение, не указав поле с ошибкой.',
      networkError: 'Сервер недоступен. Проверьте соединение и повторите попытку.',
      serverError: 'Сервер не смог обработать запрос. Повторите попытку позже.',
      displayHint: 'Настенный экран: показывает только карту рейсов. Остаётся в системе 30 дней и продлевает вход каждый день.',
      endSessionsTitle: 'Выйти на всех устройствах',
      endSessionsHint: 'Все устройства, где выполнен вход в эту учётную запись, будут выведены из системы. Учётная запись не меняется и может сразу войти снова — используйте это, если вход экрана мог быть скопирован.',
      endSessionsSelf: 'Все устройства, где вы вошли в систему, будут выведены из неё, включая это.',
      endSessions: 'Выйти на всех устройствах',
      blockTitle: 'Заблокировать учетную запись',
      blockHint: 'Пользователь не сможет войти. Ничего не удаляется.',
      block: 'Заблокировать',
      blockSelf: 'Свою учетную запись заблокировать нельзя.',
      unblockTitle: 'Учетная запись заблокирована',
      unblockHint: 'Пользователь не может войти, пока доступ не восстановлен.',
      unblock: 'Восстановить доступ',
    },
  },

  monitor: {
    title: 'Мониторинг интеграций',
    views: {
      sources: 'Источники',
      events: 'События',
      messages: 'Полученные сообщения',
    },
    errorLoad: 'Не удалось загрузить монитор. Проверьте соединение и повторите попытку.',
    errorForbidden: 'Мониторинг интеграций доступен только администратору.',
    errorFilter: 'Сервер отклонил фильтр — сохраненная ссылка может называть источник, которого больше нет. Сбросьте фильтры и повторите.',
    clearFilters: 'Сбросить все',
    fields: {
      scheduledDate: 'Время по расписанию',
      estimatedDate: 'Расчетное время',
      actualDate: 'Фактическое время',
      departedTime: 'Вылет из пункта отправления',
      flightStatus: 'Статус',
      delayMinutes: 'Задержка, мин',
      delayReason: 'Причина задержки',
      passengerAmount: 'Пассажиров',
      totalCargoWeight: 'Груз, кг',
      planeParkingName: 'Стоянка',
      externalFlightId: 'Внешний идентификатор',
      active: 'В расписании',
    },
    changes: {
      cleared: 'очищено',
    },
    payloadGone: 'Запись уже удалена по сроку хранения.',
    payloadEmpty: 'Для этой записи ничего не сохранено.',
    deliveryNoContent:
      'Содержимое этой доставки не сохраняется. Успешный снимок расписания намеренно не хранит файл целиком — запись по каждому рейсу лежит в самом событии.',
    payloadFailed: 'Не удалось загрузить.',
    health: {
      OK: 'В порядке',
      STALE: 'Нет отклика',
      FAILING: 'Сбой',
      UNKNOWN: 'Нет данных',
    },
    sources: {
      lead: 'Все источники, которые умеет запускать приложение, — доставляли они что-то или нет. Состояние оценивается по доступности источника: канал, которому нечего сообщить, работает; канал, с которым нет связи, — нет.',
      columns: {
        source: 'Источник',
        health: 'Состояние',
        interval: 'Интервал',
        trust: 'Ранг',
        updated: 'Настройки изменены',
      },
      off: 'Выключен',
      push: 'Push',
      defaults: 'По умолчанию',
      seconds: '{count} с',
      minutes: '{count} мин',
      replaying: 'Перечитывание архива: {progress}',
      replayingStart: 'Перечитывание архива…',
    },
    panel: {
      title: 'Настройки источника',
      displayName: 'Отображаемое имя',
      enabled: 'Включен',
      on: 'Вкл',
      interval: 'Ожидаемый интервал, секунды',
      intervalInvalid: 'Целое число секунд либо пусто.',
      resetDefault: 'по умолчанию {value}',
      trust: 'Ранг доверия',
      trustInvalid: 'Целое число от 0 до 100.',
      updatedBy: 'Изменил {name}, {date}',
      saved: 'Сохранено.',
      saving: 'Сохранение…',
      showErrors: 'Последние ошибки',
      replayTitle: 'Перечитать весь архив',
      replayHint: 'Прогоняет все архивные файлы через сегодняшний разбор. Только заполняет пробелы — то, что уже есть в расписании, не перезаписывается. По пять файлов за опрос, так что это небыстро, а деплой отменяет процесс.',
      replayStart: 'Начать перечитывание',
      replayCancel: 'Отменить перечитывание',
      replayRunning: 'Идет — последний завершенный файл: {progress}',
      replayRunningStart: 'Идет — ни один файл еще не завершен',
      networkError: 'Сервер недоступен. Проверьте соединение и повторите попытку.',
      serverError: 'Сервер не смог обработать запрос. Повторите попытку позже.',
    },
    events: {
      found: 'Событий',
      columns: {
        time: 'Время',
        type: 'Событие',
        flight: 'Рейс',
        details: 'Изменения',
        payload: 'Сообщение',
      },
      types: {
        CREATED: 'Создание',
        UPDATED: 'Изменение',
        DELETED: 'Удаление',
        RECEIVED: 'Получено',
        WARNING: 'Предупреждение',
        ERROR: 'Ошибка',
      },
      allSources: 'Все источники',
      allTypes: 'Все события',
      flightLabel: 'Номер рейса',
      flightExample: 'KC222',
      rowLabel: 'ID расписания',
      rowExample: '395239',
      show: 'Показать',
      hide: 'Скрыть',
      allLegs: 'Все',
      fieldLabel: 'Изменённое поле',
      allFields: 'Любое поле',
      periodLabel: 'Период',
      defaultWindow: 'Последние 48 часов',
      lastDays: 'Последние {count} дней',
      sourceRecord: 'Что источник прислал по этому рейсу',
      wholeDelivery: 'Сообщение целиком',
      window: 'Показаны последние 48 часов. Чтобы заглянуть дальше, отфильтруйте по номеру рейса или ID расписания.',
      fullHistory: 'Показана вся история этого рейса — без ограничения в 48 часов.',
      payload: 'Сообщение',
      noSource: 'без источника',
      nothingFound: 'Ни одно событие не подходит под фильтры',
    },
    messages: {
      found: 'Сообщений',
      lead: 'Все, что прислали источники, вместе с исходным содержимым — телекс, файл с FTP, XML-запрос, CSV. Опрос, содержимое которого не изменилось, не сохраняется, поэтому тихий канал — здоровый: живость видна по индикатору на «Источниках», а не по числу строк здесь.',
      columns: {
        kind: 'Тип',
        status: 'Статус',
        rows: 'Изменило / получено',
        ref: 'Ссылка',
      },
      statuses: {
        PROCESSED: 'Обработано',
        UNCHANGED: 'Без изменений',
        FAILED: 'Сбой',
      },
      nothingFound: 'Ни одно сообщение не подходит под фильтры',
    },
  },

  map: {
    title: 'Рейсы в воздухе',
    mapLabel: 'Карта рейсов в воздухе',
    showKazakhstan: 'Казахстан',
    basemapFailed: 'Не удалось загрузить контуры карты. Рейсы при этом показаны верно.',
    inAir: {
      one: '{count} рейс в воздухе',
      few: '{count} рейса в воздухе',
      many: '{count} рейсов в воздухе',
      other: '{count} рейса в воздухе',
    },
    updatedAt: 'обновлено в {time}',
    showingFrom: 'Показаны данные на {time}.',
    errorLoad: 'Не удалось обновить рейсы. Проверьте соединение — карта повторяет попытку каждую минуту.',
    errorForbidden: 'Расписание рейсов недоступно для вашей роли.',
    approximate:
      'Положение рассчитано по времени вылета и ожидаемому времени прилёта — расписание не сообщает, где находится борт.',
    withoutCoordinates: 'Не показаны на карте — нет координат в справочнике аэропортов: {codes}.',
    selectedGone: 'Рейса из этой ссылки уже нет в воздухе.',
    empty: 'Сейчас в воздухе нет рейсов.',
    minutes: '{minutes} мин',
    hoursMinutes: '{hours} ч {minutes} мин',
    inDuration: 'через {duration}',
    overdueBy: 'на {duration} позже ожидаемого',
    backToList: 'Назад к списку',
    percentFlown: 'Пройдено {percent}%',
    departed: 'Вылетел',
    expected: 'Ожидается',
    remaining: 'Осталось',
    overdue: 'Позже ожидаемого',
    awaitingLanding: 'Посадка ещё не подтверждена.',
    landed: 'Приземлился',
    landedAgo: 'приземлился {duration} назад',
    landedJustNow: 'только что приземлился',
    landedCount: {
      one: '{count} приземлился',
      few: '{count} приземлились',
      many: '{count} приземлились',
      other: '{count} приземлились',
    },
    basis: {
      LANDED: 'фактическое время посадки',
      ESTIMATED: 'расчётное время прилёта от источника',
      SCHEDULED: 'по плановому времени полёта — расчётного времени нет',
      DISTANCE: 'по расстоянию — время прилёта не сообщалось',
    },
    distance: 'Расстояние',
    km: '{distance} км',
    registration: 'Бортовой номер',
    openInSchedule: 'Открыть в расписании',
  },

  common: {
    date: 'Дата',
    time: 'Время',
    save: 'Сохранить',
    cancel: 'Отмена',
    close: 'Закрыть',
    delete: 'Удалить',
    search: 'Поиск',
    back: 'Назад',
    refresh: 'Обновить',
    loading: 'Загрузка…',
    noData: 'Нет данных',
    required: 'Обязательное поле',
    yes: 'Да',
    no: 'Нет',
  },

  schedule: {
    title: 'Расписание рейсов',
    previousPeriod: 'Предыдущий период',
    nextPeriod: 'Следующий период',
    scheduledFrom: 'По расписанию, с',
    scheduledTo: 'По расписанию, до',
    range: {
      label: 'Период',
      today: 'Сегодня',
      yesterday: 'Вчера',
      tomorrow: 'Завтра',
      last24h: 'Последние 24 часа',
      last7d: 'Последние 7 дней',
      next7d: 'Следующие 7 дней',
      last30d: 'Последние 30 дней',
      thisMonth: 'Текущий месяц',
      custom: 'Свой период',
      invalid: 'Конец периода раньше его начала.',
    },
    allTypes: 'Все',
    allStations: 'Все аэропорты',
    export: {
      label: 'Скачать',
      excel: 'Excel (.xlsx)',
      apcas: 'APCAS (.csv)',
      working: 'Подготовка…',
      error: 'Не удалось выгрузить файл. Проверьте соединение и повторите попытку.',
    },
    resetFilters: 'Сбросить все',
    clearFilter: 'Сбросить: {name}',
    noMatches: 'Авиакомпании не найдены',
    airlinesFailed: 'Список авиакомпаний не загрузился — код можно ввести вручную',
    moreMatches: 'Еще {count} — продолжайте вводить, чтобы сузить список',
    airportsFailed: 'Поиск аэропортов не удался — код можно ввести вручную',
    searching: 'Поиск…',
    laterBy: 'Позже расписания на {count} мин',
    earlierBy: 'Раньше расписания на {count} мин',
    nothingFound: 'Нет рейсов по этим фильтрам',
    statusLegend: 'Цвета статусов',
    firstPage: 'Первая страница',
    previousPage: 'Предыдущая страница',
    nextPage: 'Следующая страница',
    lastPage: 'Последняя страница',
    sortHint: 'Сортировать по этой колонке',
    perPage: 'На странице',
    pageOf: 'Страница {page} из {pages}',
    pageOnly: 'Страница {page}',
    rowsFound: 'Рейсов',
    bothLegsHint: 'Каждый рейс — это две строки, по одной на каждый аэропорт, поэтому по номеру рейса возвращаются и прилет, и вылет.',
    errorLoad: 'Не удалось загрузить расписание. Проверьте соединение и повторите попытку.',
    errorForbidden: 'Ваша роль не имеет доступа к расписанию рейсов.',
    scopes: {
      DOMESTIC: 'Внутренний',
      INTERNATIONAL: 'Международный',
    },
    detail: {
      title: 'Карточка рейса',
      thisStation: 'данный аэропорт',
      routeAlt: 'Маршрут {from} — {to}',
      withdrawn: 'Снят',
      notFound: 'Этого рейса больше нет в расписании.',
      otherLeg: 'Открыть второй отрезок — {leg} в {station}',
      history: 'История изменений',
      noHistory: 'С момента создания рейса изменений не было.',
      historyFailed: 'Историю загрузить не удалось. Сам рейс показан выше.',
      archiveNote: 'Записи с источником AIRPORT взяты из архива, который велся до перехода на события: они хранят значение на тот момент, а не само изменение, и не содержат сообщения.',
      archivedRow: 'Архивная запись: сделана до того, как изменения стали фиксироваться событиями.',
      unknownSource: 'источник неизвестен',
      events: {
        CREATED: 'Создан',
        UPDATED: 'Обновлен',
        DELETED: 'Удален',
      },
      showRaw: 'Сообщение',
      hideRaw: 'Скрыть',
      delayed: 'Задержка {count} мин',
      departedFrom: 'Вылет из пункта отправления',
      passengers: 'Пассажиры',
      cargo: 'Груз',
      kg: 'кг',
      times: { scheduled: 'По расписанию', estimated: 'Расчетное', actual: 'Фактическое' },
      errorLoad: 'Не удалось загрузить рейс. Повторите попытку.',
      errorForbidden: 'Ваша роль не имеет доступа к этому рейсу.',
    },
    columns: {
      flight: 'Номер рейса',
      airline: 'Авиакомпания',
      station: 'Аэропорт',
      leg: 'Направление',
      origin: 'Аэропорт вылета',
      destination: 'Аэропорт прилета',
      scheduled: 'Дата по расписанию',
      estimated: 'Расчетная дата',
      actual: 'Фактическая дата',
      status: 'Статус',
      stand: 'Стоянка',
      delayReason: 'Код задержки',
      passengers: 'Кол-во пассажиров',
    },
    types: {
      ARRIVAL: 'Прилет',
      DEPARTURE: 'Вылет',
    },
    statuses: {
      EXPECTED: 'Ожидается',
      ON_TIME: 'По расписанию',
      DELAYED: 'Задержан',
      CHECK_IN: 'Регистрация',
      BOARDING: 'Посадка',
      DEPARTED_EARLY: 'Вылетел раньше',
      DEPARTED_ON_TIME: 'Вылетел по расписанию',
      DEPARTED_LATE: 'Вылетел с задержкой',
      LANDED_EARLY: 'Приземлился раньше',
      LANDED_ON_TIME: 'Приземлился по расписанию',
      LANDED_LATE: 'Приземлился с задержкой',
      CANCELLED: 'Отменен',
      UNKNOWN: 'Неизвестно',
    },
  },

  flight: {
    number: 'Рейс',
    route: 'Маршрут',
    scheduled: 'По расписанию',
    estimated: 'Расчетное',
    actual: 'Фактическое',
    status: 'Статус',
    found: {
      one: '{count} рейс',
      few: '{count} рейса',
      many: '{count} рейсов',
      other: '{count} рейса',
    },
    delayedBy: {
      one: 'Задержка на {count} минуту',
      few: 'Задержка на {count} минуты',
      many: 'Задержка на {count} минут',
      other: 'Задержка на {count} минуты',
    },
  },

  // Kept here rather than taken from Intl: Chrome has no month names for
  // Kazakh and silently formats it as "2026 M07 24".
  months: {
    jan: 'января',
    feb: 'февраля',
    mar: 'марта',
    apr: 'апреля',
    may: 'мая',
    jun: 'июня',
    jul: 'июля',
    aug: 'августа',
    sep: 'сентября',
    oct: 'октября',
    nov: 'ноября',
    dec: 'декабря',
  },

  status: {
    onTime: 'По расписанию',
    delayed: 'Задержан',
    departed: 'Вылетел',
    landed: 'Приземлился',
    cancelled: 'Отменен',
    expected: 'Ожидается',
  },

  demo: {
    signedInAs: 'Вы вошли как {name}',
    lastUpdated: 'Обновлено в {time}',
    cargo: '{weight} кг груза на {count} рейсах',
  },
};
